/**
 * How a Writing prompt is set.
 *
 * The paper prints a Task 2 topic in bold and the sentences that frame it in
 * roman: "Write about the following topic:" above, and the line about giving
 * reasons below. Task 1 is bold throughout — its own instruction to summarise
 * and compare carries the same weight as the sentence describing the chart.
 *
 * The importer reads plain text and cannot see the weight the document used,
 * so the fixed sentences are recognised here by their wording. They are fixed
 * in the real exam, printed identically on every paper.
 */

const FRAMING = [
  /^write about the following topic:?$/i,
  /^give reasons for your answer and include any relevant examples from your own knowledge (?:and|or) experience\.?$/i,
];

const PARAGRAPH = /<p([^>]*)>([\s\S]*?)<\/p>/g;

/** The text of a paragraph, with its markup and spacing taken out. */
function textOf(inner: string): string {
  return inner
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Adds a class to a tag's existing attributes, or gives it one. */
function withClass(attrs: string, name: string): string {
  const existing = /class="([^"]*)"/.exec(attrs);
  if (!existing) return `${attrs} class="${name}"`;
  return attrs.replace(existing[0], `class="${existing[1]} ${name}"`);
}

/**
 * Marks the framing sentences of a Writing prompt so they can be set in roman
 * while the topic between them stays bold.
 */
export function markPromptFraming(html: string): string {
  return html.replace(PARAGRAPH, (whole, attrs: string, inner: string) => {
    const text = textOf(inner);
    if (!FRAMING.some((pattern) => pattern.test(text))) return whole;
    return `<p${withClass(attrs, 'xprompt__framing')}>${inner}</p>`;
  });
}

const LEAD_IN = 'Write about the following topic:';
const CLOSING =
  'Give reasons for your answer and include any relevant examples from your own knowledge or experience.';

/**
 * The line under every Academic Task 1 chart, in the exam's own spelling.
 *
 * British, because IELTS is: the paper reads "Summarise". A paper carrying its
 * own copy keeps it, spelled either way — see the check in writingPromptHtml —
 * and only a paper with no line at all is given this one.
 *
 * Bold, like the sentence above it. This is not framing in the sense the Task
 * 2 sentences are: it is half the task. The band descriptors for Task 1 are
 * written against "selecting and reporting the main features" and "making
 * comparisons", so a candidate who never read it is being marked on an
 * instruction they were not given.
 */
const TASK_1_ACADEMIC =
  'Summarise the information by selecting and reporting the main features, and make comparisons where relevant.';

/**
 * Every block of the prompt that holds a sentence: its paragraphs, and its
 * headings too.
 *
 * The platform's own template writes Task 1's instruction as a heading —
 * "## Summarise the information…" — which the importer sets as an <h4>. This
 * used to look for the paper's own copy in paragraphs only, so it never found
 * that one and printed the instruction a second time underneath, in the exam
 * as well as on the review.
 */
const BLOCK = /<(p|h[1-6])\b[^>]*>([\s\S]*?)<\/\1>/gi;

/** Whether any paragraph or heading of the prompt opens with these words. */
function has(html: string, opening: RegExp): boolean {
  for (const match of html.matchAll(BLOCK)) {
    if (opening.test(textOf(match[2] ?? ''))) return true;
  }
  return false;
}

/**
 * A Writing prompt as the screen shows it.
 *
 * Every Writing paper carries fixed sentences around its topic, and the real
 * screen shows them whether or not whoever typed the paper included them. A
 * paper imported without them leaves a candidate reading a bare statement with
 * no instruction attached — a Task 2 topic with nothing telling them to argue
 * it, or an Academic Task 1 chart with nothing telling them to summarise and
 * compare, which is the sentence the band descriptors are written against.
 *
 * So they are supplied here, in roman, worded as the printed rubric words
 * them. A paper carrying its own version of any of them keeps it.
 *
 * General Training Task 1 is a letter and has no summarising line: its frame
 * is the "Dear ..." opening the paper itself prints, so nothing is added.
 */
export function writingPromptHtml(
  html: string,
  task: 1 | 2,
  variant: 'ACADEMIC' | 'GENERAL' = 'ACADEMIC',
): string {
  const marked = markPromptFraming(html);
  const framed = (text: string) => `<p class="xprompt__framing">${text}</p>`;

  if (task === 1) {
    if (variant !== 'ACADEMIC') return marked;
    if (has(marked, /^summari[sz]e the information/i)) return marked;
    // Bold, so no framing class: it is set as the prompt above it is set.
    return `${marked}<p>${TASK_1_ACADEMIC}</p>`;
  }

  const lead = has(marked, /^write about the following/i) ? '' : framed(LEAD_IN);
  const close = has(marked, /^give reasons/i) ? '' : framed(CLOSING);
  return `${lead}${marked}${close}`;
}
